import { useState } from "react";
import { caseStudies } from "../data/mock";
import {
  ArrowUpRight, X, Quote, Clock, Users, Building2,
  ChevronRight, TrendingUp, ExternalLink
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const FeaturedCard = ({ study, onOpen }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="relative rounded-2xl overflow-hidden cursor-pointer mb-10"
      style={{
        background: "#121530",
        border: `1px solid ${isHovered ? "rgba(123,104,238,0.3)" : "rgba(123,104,238,0.1)"}`,
        transition: "border-color 0.4s ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onOpen(study)}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Image Side */}
        <div className="relative overflow-hidden" style={{ minHeight: "360px" }}>
          <img
            src={study.image}
            alt={study.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
            style={{
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              filter: "brightness(0.45) contrast(1.15)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(18,21,48,0.95) 0%, rgba(18,21,48,0.4) 100%)" }}
          />
          {/* Featured badge */}
          <div
            className="absolute top-6 left-6 flex items-center gap-2 rounded-md"
            style={{
              padding: "6px 14px",
              background: "rgba(123,104,238,0.25)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(123,104,238,0.3)",
            }}
          >
            <TrendingUp size={13} style={{ color: "#A78BFA" }} />
            <span style={{ fontFamily: "'Kode Mono', monospace", fontSize: "11px", color: "#C4B5FD", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Featured Project
            </span>
          </div>
          {/* Results overlay on image */}
          <div className="absolute bottom-6 left-6 right-6">
            <div className="grid grid-cols-2 gap-3">
              {study.results.slice(0, 2).map((r, i) => (
                <div
                  key={i}
                  className="rounded-lg"
                  style={{
                    padding: "14px 16px",
                    background: "rgba(15,18,37,0.7)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(123,104,238,0.15)",
                  }}
                >
                  <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "22px", fontWeight: 700, color: "#7B68EE", lineHeight: 1 }}>
                    {r.metric}
                  </div>
                  <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "11px", color: "#8585A8", marginTop: "4px" }}>
                    {r.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content Side */}
        <div style={{ padding: "40px 40px 36px" }}>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span
              className="rounded-md"
              style={{ padding: "4px 12px", fontSize: "12px", fontFamily: "'Kode Mono', monospace", background: "rgba(96,165,250,0.1)", color: "#93C5FD", border: "1px solid rgba(96,165,250,0.15)" }}
            >
              {study.industry}
            </span>
            <span className="flex items-center gap-1" style={{ fontSize: "12px", fontFamily: "'Kode Mono', monospace", color: "#6B6B8D" }}>
              <Clock size={12} /> {study.duration}
            </span>
            <span className="flex items-center gap-1" style={{ fontSize: "12px", fontFamily: "'Kode Mono', monospace", color: "#6B6B8D" }}>
              <Users size={12} /> {study.teamSize}
            </span>
          </div>

          <h3
            className="mb-2"
            style={{ fontFamily: "'Kode Mono', monospace", fontSize: "clamp(22px, 2.5vw, 28px)", fontWeight: 600, color: "#F0F0FF", lineHeight: 1.25 }}
          >
            {study.title}
          </h3>
          <p
            className="mb-5"
            style={{ fontFamily: "'Kode Mono', monospace", fontSize: "13px", color: "#8585A8", lineHeight: 1.5 }}
          >
            {study.client}
          </p>

          <p
            className="mb-6"
            style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", color: "#9494B8", lineHeight: 1.7, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          >
            {study.challenge}
          </p>

          {/* Results row */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {study.results.map((r, i) => (
              <div key={i} className="text-center">
                <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "18px", fontWeight: 700, color: "#7B68EE", lineHeight: 1.2 }}>
                  {r.metric}
                </div>
                <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "10px", color: "#6B6B8D", marginTop: "4px", lineHeight: 1.3 }}>
                  {r.label}
                </div>
              </div>
            ))}
          </div>

          {/* Tech stack */}
          <div className="flex flex-wrap gap-2 mb-6">
            {study.technologies.map((tech, i) => (
              <span
                key={i}
                className="rounded-md"
                style={{ padding: "3px 10px", fontSize: "11px", fontFamily: "'Kode Mono', monospace", background: "rgba(123,104,238,0.06)", color: "#8585A8", border: "1px solid rgba(123,104,238,0.1)" }}
              >
                {tech}
              </span>
            ))}
          </div>

          {/* CTA */}
          <div
            className="flex items-center gap-2 transition-all duration-300"
            style={{ fontFamily: "'Kode Mono', monospace", fontSize: "15px", fontWeight: 500, color: isHovered ? "#A78BFA" : "#7B68EE" }}
          >
            Read Full Case Study
            <ArrowUpRight size={16} className="transition-transform duration-300" style={{ transform: isHovered ? "translate(2px,-2px)" : "translate(0,0)" }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CaseCard = ({ study, index, onOpen }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 35 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative rounded-2xl overflow-hidden cursor-pointer"
      style={{
        background: "#121530",
        border: `1px solid ${isHovered ? "rgba(123,104,238,0.28)" : "rgba(123,104,238,0.08)"}`,
        transition: "all 0.4s ease-in-out",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onOpen(study)}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: "190px" }}>
        <img
          src={study.image}
          alt={study.title}
          className="w-full h-full object-cover transition-transform duration-700"
          style={{ transform: isHovered ? "scale(1.06)" : "scale(1)", filter: "brightness(0.4) contrast(1.15)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,21,48,0.2) 0%, rgba(18,21,48,0.95) 100%)" }} />
        {/* Industry badge */}
        <div
          className="absolute top-4 left-4 rounded-md"
          style={{ padding: "4px 12px", fontSize: "11px", fontFamily: "'Kode Mono', monospace", background: "rgba(96,165,250,0.15)", backdropFilter: "blur(8px)", color: "#93C5FD", border: "1px solid rgba(96,165,250,0.2)" }}
        >
          {study.industry}
        </div>
        {/* Mini results on image */}
        <div className="absolute bottom-4 left-4 right-4 flex gap-2">
          {study.results.slice(0, 2).map((r, i) => (
            <div
              key={i}
              className="flex-1 rounded-md"
              style={{ padding: "8px 10px", background: "rgba(15,18,37,0.7)", backdropFilter: "blur(10px)", border: "1px solid rgba(123,104,238,0.12)" }}
            >
              <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "16px", fontWeight: 700, color: "#7B68EE", lineHeight: 1 }}>
                {r.metric}
              </div>
              <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "9px", color: "#6B6B8D", marginTop: "2px" }}>
                {r.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 24px 22px" }}>
        <div className="flex items-center gap-3 mb-3">
          <span className="flex items-center gap-1" style={{ fontSize: "11px", fontFamily: "'Kode Mono', monospace", color: "#6B6B8D" }}>
            <Clock size={11} /> {study.duration}
          </span>
          <span className="flex items-center gap-1" style={{ fontSize: "11px", fontFamily: "'Kode Mono', monospace", color: "#6B6B8D" }}>
            <Users size={11} /> {study.teamSize}
          </span>
        </div>

        <h3
          className="mb-2"
          style={{ fontFamily: "'Kode Mono', monospace", fontSize: "17px", fontWeight: 600, color: "#F0F0FF", lineHeight: 1.3 }}
        >
          {study.title}
        </h3>
        <p
          className="mb-4"
          style={{ fontFamily: "'Kode Mono', monospace", fontSize: "12px", color: "#8585A8", lineHeight: 1.4 }}
        >
          {study.client}
        </p>

        {/* Tech pills */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {study.technologies.slice(0, 4).map((tech, i) => (
            <span
              key={i}
              className="rounded-md"
              style={{ padding: "2px 8px", fontSize: "10px", fontFamily: "'Kode Mono', monospace", background: "rgba(123,104,238,0.06)", color: "#6B6B8D", border: "1px solid rgba(123,104,238,0.08)" }}
            >
              {tech}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div
          className="flex items-center justify-between pt-4"
          style={{ borderTop: "1px solid rgba(123,104,238,0.08)" }}
        >
          <span
            className="flex items-center gap-2 transition-all duration-300"
            style={{ fontFamily: "'Kode Mono', monospace", fontSize: "13px", fontWeight: 500, color: isHovered ? "#A78BFA" : "#7B68EE" }}
          >
            View Case Study
            <ChevronRight size={14} className="transition-transform duration-300" style={{ transform: isHovered ? "translateX(3px)" : "translateX(0)" }} />
          </span>
        </div>
      </div>

      {/* Top accent */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] transition-all duration-500"
        style={{ background: "linear-gradient(90deg, #7B68EE, #60A5FA)", opacity: isHovered ? 1 : 0 }}
      />
    </motion.div>
  );
};

const CaseDetailModal = ({ study, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{ background: "rgba(10,12,28,0.88)", backdropFilter: "blur(10px)", padding: "40px 20px" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="relative w-full rounded-2xl overflow-hidden"
        style={{ maxWidth: "1000px", background: "#121530", border: "1px solid rgba(123,104,238,0.2)", marginTop: "30px", marginBottom: "40px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full border-none cursor-pointer transition-all duration-300"
          style={{ background: "rgba(15,18,37,0.85)", backdropFilter: "blur(8px)", border: "1px solid rgba(123,104,238,0.2)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(123,104,238,0.3)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15,18,37,0.85)"; }}
        >
          <X size={18} style={{ color: "#C4B5FD" }} />
        </button>

        {/* Hero Image */}
        <div className="relative" style={{ height: "300px" }}>
          <img src={study.image} alt={study.title} className="w-full h-full object-cover" style={{ filter: "brightness(0.4) contrast(1.15)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,21,48,0.15) 0%, rgba(18,21,48,1) 100%)" }} />
          <div className="absolute bottom-0 left-0 right-0 p-10 pb-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="rounded-md" style={{ padding: "5px 14px", fontSize: "12px", fontFamily: "'Kode Mono', monospace", background: "rgba(96,165,250,0.15)", color: "#93C5FD", border: "1px solid rgba(96,165,250,0.2)" }}>
                {study.industry}
              </span>
              <span className="flex items-center gap-1.5" style={{ fontSize: "12px", fontFamily: "'Kode Mono', monospace", color: "#8585A8" }}>
                <Clock size={13} /> {study.duration}
              </span>
              <span className="flex items-center gap-1.5" style={{ fontSize: "12px", fontFamily: "'Kode Mono', monospace", color: "#8585A8" }}>
                <Users size={13} /> {study.teamSize}
              </span>
            </div>
            <h2 style={{ fontFamily: "'Kode Mono', monospace", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 600, color: "#F0F0FF", lineHeight: 1.2, margin: 0 }}>
              {study.title}
            </h2>
            <p style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", color: "#8585A8", marginTop: "8px" }}>
              {study.client}
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "8px 40px 44px" }}>
          {/* Results Banner */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 rounded-xl"
            style={{ padding: "28px", background: "rgba(123,104,238,0.05)", border: "1px solid rgba(123,104,238,0.1)" }}
          >
            {study.results.map((r, i) => (
              <div key={i} className="text-center">
                <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, color: "#7B68EE", lineHeight: 1.1 }}>
                  {r.metric}
                </div>
                <div style={{ fontFamily: "'Kode Mono', monospace", fontSize: "12px", color: "#8585A8", marginTop: "6px" }}>
                  {r.label}
                </div>
              </div>
            ))}
          </div>

          {/* Challenge & Solution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div>
              <h4 className="flex items-center gap-2 mb-4" style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", fontWeight: 600, color: "#C4B5FD", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <div className="w-2 h-2 rounded-full" style={{ background: "#EF4444" }} />
                The Challenge
              </h4>
              <p style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", color: "#9494B8", lineHeight: 1.8 }}>
                {study.challenge}
              </p>
            </div>
            <div>
              <h4 className="flex items-center gap-2 mb-4" style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", fontWeight: 600, color: "#93C5FD", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <div className="w-2 h-2 rounded-full" style={{ background: "#7B68EE" }} />
                Our Solution
              </h4>
              <p style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", color: "#9494B8", lineHeight: 1.8 }}>
                {study.solution}
              </p>
            </div>
          </div>

          {/* Technologies Used */}
          <div className="mb-10">
            <h4 className="mb-4" style={{ fontFamily: "'Kode Mono', monospace", fontSize: "13px", fontWeight: 600, color: "#6B6B8D", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Technologies Used
            </h4>
            <div className="flex flex-wrap gap-2">
              {study.technologies.map((tech, i) => (
                <span
                  key={i}
                  className="rounded-lg"
                  style={{ padding: "8px 16px", fontSize: "13px", fontFamily: "'Kode Mono', monospace", background: "rgba(123,104,238,0.08)", color: "#B8B8D0", border: "1px solid rgba(123,104,238,0.15)" }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div
            className="rounded-xl mb-8"
            style={{ padding: "32px", background: "rgba(123,104,238,0.04)", border: "1px solid rgba(123,104,238,0.1)" }}
          >
            <Quote size={28} style={{ color: "rgba(123,104,238,0.25)", marginBottom: "16px" }} />
            <p
              className="mb-4"
              style={{ fontFamily: "'Kode Mono', monospace", fontSize: "clamp(15px, 2vw, 18px)", color: "#C4C4E0", lineHeight: 1.7, fontStyle: "italic" }}
            >
              "{study.testimonial.quote}"
            </p>
            <p style={{ fontFamily: "'Kode Mono', monospace", fontSize: "13px", color: "#7B68EE", fontWeight: 500 }}>
              — {study.testimonial.author}
            </p>
          </div>

          {/* CTA */}
          <div
            className="flex items-center justify-between flex-wrap gap-4 pt-6"
            style={{ borderTop: "1px solid rgba(123,104,238,0.1)" }}
          >
            <p style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", color: "#6B6B8D", margin: 0 }}>
              Want similar results for your business?
            </p>
            <button
              onClick={() => { onClose(); setTimeout(() => { const el = document.querySelector("#contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 300); }}
              className="inline-flex items-center gap-3 border-none cursor-pointer rounded-lg"
              style={{ background: "#7B68EE", color: "#FFFFFF", padding: "12px 24px", fontSize: "15px", fontWeight: 500, fontFamily: "'Kode Mono', monospace", transition: "background 0.3s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#6A58DD"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#7B68EE"; }}
            >
              Start Your Project
              <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const CaseStudiesSection = () => {
  const [selectedStudy, setSelectedStudy] = useState(null);

  const featured = caseStudies.find((s) => s.featured);
  const others = caseStudies.filter((s) => !s.featured);

  return (
    <>
      <section
        id="case-studies"
        className="relative w-full"
        style={{ background: "#0F1225", padding: "120px 7.6923%" }}
      >
        <div className="max-w-[1400px] mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] rounded-full" style={{ background: "#7B68EE" }} />
              <span
                className="text-sm tracking-widest uppercase"
                style={{ color: "#7B68EE", fontFamily: "'Kode Mono', monospace" }}
              >
                Proven Results
              </span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <h2
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontWeight: 600,
                    fontSize: "clamp(32px, 4vw, 48px)",
                    color: "#F0F0FF",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                    marginBottom: "16px",
                  }}
                >
                  Case Studies
                </h2>
                <p
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontSize: "17px",
                    color: "#8585A8",
                    maxWidth: "650px",
                    lineHeight: 1.6,
                  }}
                >
                  Real projects. Measurable outcomes. Explore how we've helped organizations
                  across industries achieve transformative results through technology.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={16} style={{ color: "#6B6B8D" }} />
                <span style={{ fontFamily: "'Kode Mono', monospace", fontSize: "14px", color: "#6B6B8D" }}>
                  {caseStudies.length} Projects Showcased
                </span>
              </div>
            </div>
          </motion.div>

          {/* Featured Case Study */}
          {featured && (
            <FeaturedCard study={featured} onOpen={setSelectedStudy} />
          )}

          {/* Other Case Studies Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {others.map((study, index) => (
              <CaseCard
                key={study.id}
                study={study}
                index={index}
                onOpen={setSelectedStudy}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedStudy && (
          <CaseDetailModal
            study={selectedStudy}
            onClose={() => setSelectedStudy(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};
